// Matrix-style green "digital rain": a full-screen canvas overlay (pointer-events:none) that runs for
// a few seconds and then fades out and removes itself. Pure DOM/canvas — no dependencies.
//
// One column at a time writes an encouraging phrase instead of random glyphs: the phrase falls exactly
// like the rain (one bright letter drawn per step at the head, top letter first), so it reads top-down
// with a natural fading trail and never ghosts. When a phrase finishes, the next one starts elsewhere.
const PHRASES = [
    'THANK YOU',
    'YOU ARE THE BEST',
    'CONTINUE THIS WAY',
    'GREAT WORK',
    'KEEP IT UP',
    'WELL DONE',
    'YOU ROCK',
    'YOU CAN DO IT',
    'YES YOU CAN',
    'BELIEVE IN YOURSELF',
    'STAY AWESOME',
];

export function matrixRain(durationMs = 7000) {
    if (typeof document === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = document.createElement('canvas');
    canvas.style.cssText =
        'position:fixed;inset:0;z-index:9999;pointer-events:none;transition:opacity 600ms ease;opacity:1;';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        canvas.remove();
        return;
    }
    // Top-aligned text so row 0 (the first letter of each phrase) is on-screen, not clipped above.
    ctx.textBaseline = 'top';

    const dpr = window.devicePixelRatio || 1;
    const fontSize = 18;
    const cell = fontSize * dpr;
    const startOffset = 3; // begin the phrase a few rows down so its first letter clears the top edge
    const chars = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄ0123456789ABCDEF'.split('');

    let width = 0;
    let height = 0;
    let columns = 0;
    let drops: number[] = []; // head row per column
    let phrase: (string | null)[] = []; // phrase this column is writing from its top, or null for rain
    let active = -1; // the single column currently carrying a phrase

    const seedPhrase = () => {
        const c = Math.floor(Math.random() * columns);
        phrase[c] = PHRASES[Math.floor(Math.random() * PHRASES.length)];
        drops[c] = 0; // start writing from the top
        active = c;
    };

    const resize = () => {
        width = canvas.width = Math.floor(window.innerWidth * dpr);
        height = canvas.height = Math.floor(window.innerHeight * dpr);
        canvas.style.width = `${window.innerWidth}px`;
        canvas.style.height = `${window.innerHeight}px`;
        columns = Math.max(1, Math.floor(width / cell));
        drops = new Array(columns).fill(0).map(() => Math.floor(Math.random() * -50));
        phrase = new Array(columns).fill(null);
        active = -1;
    };
    resize();
    window.addEventListener('resize', resize);

    const step = (elapsed: number) => {
        // Translucent black over the previous frame → the classic fading trails.
        ctx.fillStyle = 'rgba(0, 0, 0, 0.09)';
        ctx.fillRect(0, 0, width, height);
        ctx.font = `${cell}px monospace`;

        for (let i = 0; i < columns; i++) {
            const r = drops[i];
            const word = phrase[i];

            if (word && r >= startOffset) {
                // The message column streams the phrase (repeating, with a gap) down the column as it
                // falls — one bright letter per step at the head — starting a few rows down so the first
                // letter clears the top edge. Reads top-down and never ghosts.
                const expanded = `${word}   `;
                const ch = expanded[(r - startOffset) % expanded.length];
                if (ch !== ' ') {
                    ctx.fillStyle = '#eafff0';
                    ctx.fillText(ch, i * cell, r * cell);
                }
            } else {
                // Plain rain glyph.
                ctx.fillStyle = '#00ff41';
                ctx.fillText(chars[Math.floor(Math.random() * chars.length)], i * cell, r * cell);
            }

            if (r * cell > height && Math.random() > 0.975) {
                drops[i] = 0;
                if (phrase[i]) {
                    phrase[i] = null;
                    if (active === i) active = -1;
                }
            }
            drops[i]++;
        }

        if (elapsed > warmupMs && active === -1) seedPhrase();
    };

    // Throttle to a calm, readable pace rather than the full-60fps blur (~14 rows/sec).
    const stepMs = 70;
    const warmupMs = 1400; // let the rain fill the screen before the first phrase appears
    let raf = 0;
    let last = 0;
    let start = 0;
    const loop = (t: number) => {
        raf = requestAnimationFrame(loop);
        if (!start) start = t;
        if (t - last < stepMs) return;
        last = t;
        step(t - start);
    };
    raf = requestAnimationFrame(loop);

    window.setTimeout(() => {
        canvas.style.opacity = '0';
        window.setTimeout(() => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            canvas.remove();
        }, 600);
    }, durationMs);
}
